import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { supabase } from '../lib/supabase';
import { BRAND, PRODUCT } from '../config';

// Landing page for the Supabase password-recovery email link.
// Because the client runs with detectSessionInUrl:false, we parse the recovery
// tokens out of the URL ourselves and call setSession() to get a short-lived
// session, which then authorizes updateUser({ password }).
type Phase = 'verifying' | 'form' | 'done' | 'invalid';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('verifying');
  const [invalidMsg, setInvalidMsg] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const establish = async () => {
      // Recovery links arrive as a hash fragment:
      //   #access_token=...&refresh_token=...&type=recovery
      // (or an error: #error=...&error_description=...)
      const hash = window.location.hash.replace(/^#/, '');
      const params = new URLSearchParams(hash);
      const errDesc = params.get('error_description');
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (errDesc) {
        if (!cancelled) {
          setInvalidMsg(decodeURIComponent(errDesc.replace(/\+/g, ' ')));
          setPhase('invalid');
        }
        return;
      }

      if (accessToken && refreshToken) {
        const { error: sessErr } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (cancelled) return;
        if (sessErr) {
          setInvalidMsg('Der Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.');
          setPhase('invalid');
          return;
        }
        // Strip the tokens from the address bar.
        window.history.replaceState(null, '', window.location.pathname);
        setPhase('form');
        return;
      }

      // PKCE-style link: a ?code= query param to exchange for a session.
      const code = new URLSearchParams(window.location.search).get('code');
      if (code) {
        const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (exErr) {
          setInvalidMsg('Der Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.');
          setPhase('invalid');
          return;
        }
        window.history.replaceState(null, '', window.location.pathname);
        setPhase('form');
        return;
      }

      // No tokens in the URL — maybe a recovery session already exists.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        setPhase('form');
      } else {
        setInvalidMsg('Kein gültiger Wiederherstellungs-Link. Bitte fordere einen neuen an.');
        setPhase('invalid');
      }
    };

    establish();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setSaving(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;
      setPhase('done');
    } catch (err: any) {
      setError(err?.message || 'Das Passwort konnte nicht geändert werden.');
    } finally {
      setSaving(false);
    }
  };

  const Icon = PRODUCT === 'skonto' ? ReceiptLongIcon : RestaurantIcon;

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: 'primary.main',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Icon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700}>{BRAND.name}</Typography>
            <Typography variant="body2" color="text.secondary">Passwort zurücksetzen</Typography>
          </Box>

          {phase === 'verifying' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Link wird geprüft…
              </Typography>
            </Box>
          )}

          {phase === 'invalid' && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>{invalidMsg}</Alert>
              <Button fullWidth variant="contained" onClick={() => navigate('/login')}
                sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}>
                Zurück zur Anmeldung
              </Button>
            </>
          )}

          {phase === 'form' && (
            <form onSubmit={handleSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bitte vergib ein neues Passwort für dein Konto.
              </Typography>
              <TextField
                label="Neues Passwort"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                autoFocus
                autoComplete="new-password"
              />
              <TextField
                label="Passwort bestätigen"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={saving}
                sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Passwort speichern'}
              </Button>
            </form>
          )}

          {phase === 'done' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Dein Passwort wurde geändert. Du bist jetzt angemeldet.
              </Alert>
              <Button fullWidth variant="contained" onClick={() => navigate('/')}
                sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}>
                Weiter zur App
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
